import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import {
  ALL_CHANNELS,
  addIsoDays,
  daysInRange,
  isCalendarIsoDate,
  visibleRange,
  type ChannelSlug,
} from './calendarHelpers';
import {
  calendarModeFor,
  type CalendarDensity,
  type CalendarSearch,
  type CalendarZoom,
} from './calendarSearch';
import { MAX_VIEW_DAYS } from './ViewRangeCalendar';

const ALL_CHANNEL_SLUGS = new Set<ChannelSlug>(ALL_CHANNELS.map((channel) => channel.slug));
// Feste Längen und „Heute“ beenden einen frei gewählten Zeitraum.
const WITHOUT_RANGE = { from: undefined, to: undefined };

/**
 * Der Arbeitskontext der Belegung aus der Adresse: Zeitraum, Länge, Dichte am Handy, Aufenthalte,
 * Kanäle und Modus. Jede Änderung geht in die URL, damit Zurück, Neuladen und ein geteilter Link
 * dieselbe Ansicht wiederherstellen.
 */
export function useCalendarView(fallbackToday: string, operations: boolean) {
  const search = useSearch({ strict: false }) as CalendarSearch;
  const mobile = useMediaQuery('(max-width: 767px)');
  const navigate = useNavigate();
  const focus = isCalendarIsoDate(search.focus) ? search.focus : fallbackToday;
  const density: CalendarDensity = search.density ?? 7;
  const zoom: CalendarZoom = search.zoom ?? (mobile && density === 14 ? 14 : mobile ? 7 : 14);
  const custom =
    search.from &&
    search.to &&
    search.from <= search.to &&
    daysInRange(search.from, search.to) <= MAX_VIEW_DAYS
      ? { from: search.from, to: search.to }
      : null;
  const { from, to } = custom ?? visibleRange(focus, zoom);
  const dayCount = daysInRange(from, to);
  const [enabledChannels, setEnabledChannels] = useState<Set<ChannelSlug>>(() => {
    const restored = search.channels
      ?.split(',')
      .filter((value): value is ChannelSlug => ALL_CHANNEL_SLUGS.has(value as ChannelSlug));
    return new Set(restored?.length ? restored : ALL_CHANNEL_SLUGS);
  });

  const patch = (next: Partial<CalendarSearch>) =>
    void navigate({ to: '/calendar', search: { ...search, ...next } as never });
  // Der Fokus wandert mit: eine feste Länge setzt danach dort fort, wo man gerade schaut.
  const showRange = (range: { from: string; to: string }) =>
    patch({ from: range.from, to: range.to, focus: range.from });

  return {
    search,
    mobile,
    focus,
    density,
    zoom,
    custom: custom !== null,
    from,
    to,
    dayCount,
    stayFilter: search.stay ?? 'all',
    mode: calendarModeFor(search.mode, operations),
    enabledChannels,
    patch,
    showRange,
    previous: () =>
      custom
        ? showRange({ from: addIsoDays(from, -dayCount), to: addIsoDays(to, -dayCount) })
        : patch({ focus: addIsoDays(focus, -Math.floor(zoom / 2)) }),
    next: () =>
      custom
        ? showRange({ from: addIsoDays(from, dayCount), to: addIsoDays(to, dayCount) })
        : patch({ focus: addIsoDays(focus, Math.floor(zoom / 2)) }),
    goToday: (today: string) => patch({ focus: today, ...WITHOUT_RANGE }),
    setZoom: (value: CalendarZoom) => patch({ zoom: value, ...WITHOUT_RANGE }),
    setDensity: (value: CalendarDensity) =>
      patch({
        density: value === 7 ? undefined : value,
        zoom: value === 14 ? 14 : 7,
        ...WITHOUT_RANGE,
      }),
    toggleChannel: (slug: ChannelSlug) => {
      const nextSet = new Set(enabledChannels);
      if (nextSet.has(slug)) nextSet.delete(slug);
      else nextSet.add(slug);
      setEnabledChannels(nextSet);
      patch({ channels: [...nextSet].join(',') || undefined });
    },
    resetChannels: () => {
      setEnabledChannels(new Set(ALL_CHANNEL_SLUGS));
      patch({ channels: undefined });
    },
  };
}

export type CalendarView = ReturnType<typeof useCalendarView>;
